/* eslint-disable jsx-a11y/anchor-is-valid */
import ApexCharts, {ApexOptions} from 'apexcharts'
import clsx from 'clsx'
import React, {useEffect, useMemo, useRef} from 'react'
import {getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import LoadingChart from '../../apps/dashboard/components/LoadingChart'

type Props = {
  className: string
  title: string
  subTitle: string
  chartColor: string
  chartHeight: string
  bar: number | null | undefined
  actual: string
  target: string
  layout?: 'vertical' | 'horizontal'
  isLoading?: boolean
}

const AchievementProgress: React.FC<Props> = ({
  className,
  title,
  subTitle,
  chartColor,
  chartHeight,
  bar,
  actual,
  target,
  layout = 'vertical',
  isLoading = false,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const chart = new ApexCharts(chartRef.current, chartOptions(chartColor, chartHeight, bar))
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, bar])

  const columnLayout = useMemo(
    () => clsx({col: layout === 'horizontal', 'col-12': layout === 'vertical'}),
    [layout]
  )

  return (
    <div className={`card ${className}`}>
      {/* begin::Beader */}
      <div className='card-header border-0 py-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{title}</span>
          <span className='text-muted fw-semibold fs-7'>{subTitle}</span>
        </h3>

        {/* <div className='card-toolbar'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
          >
            <KTIcon iconName='category' className='fs-2' />
          </button>
          <Dropdown1 />
        </div> */}
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className={columnLayout}>
        {isLoading && <LoadingChart />}
        {chartRef === null && <label className='form-label'>no result data</label>}
        {!isLoading && (
          <div ref={chartRef} id='kt_charts_widget_1_chart' style={{height: chartHeight}} />
        )}
      </div>
      {!isLoading && (
        <div className='card-body d-flex flex-column'>
          <div className='flex-grow-1'>
            <div ref={chartRef} className='mixed-widget-4-chart'></div>
          </div>

          <div className='pt-5 d-flex gap-10 justify-content-center'>
            <div>
              <p className='text-gray-500 m-0 fs-5'>Actual</p>
              <p className='fs-3 m-0'>{actual}</p>
            </div>
            <div>
              <p className='text-gray-500 m-0 fs-5'>Target</p>
              <p className='fs-3 m-o'>{target}</p>
            </div>
            {/* <a href='#' className={`btn btn-${chartColor} w-100 py-3`}>
            Take Action
          </a> */}
          </div>
        </div>
      )}

      {/* end::Body */}
    </div>
  )
}

const chartOptions = (
  chartColor: string,
  chartHeight: string,
  bar: number | null | undefined
): ApexOptions => {
  const baseColor = getCSSVariableValue('--bs-' + chartColor)
  const lightColor = getCSSVariableValue('--bs-' + chartColor + '-light')
  const labelColor = getCSSVariableValue('--bs-gray-700')

  return {
    series: [bar ? bar : 0],
    chart: {
      fontFamily: 'inherit',
      height: chartHeight,
      type: 'radialBar',
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: '65%',
        },
        dataLabels: {
          name: {
            show: false,
            fontWeight: '700',
          },
          value: {
            color: labelColor,
            fontSize: '30px',
            fontWeight: '700',
            offsetY: 12,
            show: true,
            formatter: function (val) {
              return val + '%'
            },
          },
        },
        track: {
          background: lightColor,
          strokeWidth: '100%',
        },
      },
    },
    colors: [baseColor],
    stroke: {
      lineCap: 'round',
    },
    labels: ['Progress'],
  }
}

export {AchievementProgress}
